import GameModeConfigPanel from './GameModeConfigPanel';
import React, { useEffect, useRef } from 'react'
import GameModeButton from './GameModeButton';
import { getAllGameModes } from '@/lib/types/common/GameModeAdvancedSettings';
import PanelFFXIV from '@/app/components/PanelFFXIV';
import VerticalGoldenSeparator from '@/app/components/VerticalGoldenSeparator';
import { motion, AnimatePresence } from 'framer-motion';
import SeedModePanel from './SeedModePanel';
import SeedModeButton from './SeedModeButton';
import { useGame } from '@/app/providers/GameContextProvider';
import { GameMode } from '@/lib/types/common/GameMode';
import { useDeviceInfo } from '@/hooks/useIsMobile';
import GameModeConfigSeparator from './GameModeConfigSeparator';
import { useTranslation } from '@/hooks/useTranslation';
import { AUDIO, useAudio } from '@/app/providers/AudioProvider';


const GameModesSoniop = () => {
	const { room, gameState } = useGame();
	const availableGameModes = getAllGameModes();
	const hasRestoredGameMode = useRef(false);
	const { isMobile } = useDeviceInfo();
	const { t } = useTranslation();
	const currentYear = new Date().getFullYear();
	const { play } = useAudio();

	// Seed panel visibility is synced via gameState
	const showSeedPanel = gameState?.showSeedPanel || false;
	const isGameMaster = room?.sessionId === gameState?.gameMasterId;

	const handleToggleSeedPanel = (show: boolean) => {
		if (!room || !isGameMaster) return;
		room.send("toggleSeedPanel", { show });
	};

	// Restore saved game mode, expansions, and map types for game master on reconnection/page reload
	useEffect(() => {
		if (!room || !gameState || hasRestoredGameMode.current) return;

		const isGameMaster = room.sessionId === gameState.gameMasterId;
		if (!isGameMaster) return;

		// Get saved game mode from localStorage
		const savedGameMode = typeof window !== 'undefined'
			? localStorage.getItem('eorguessr_selected_gamemode') as GameMode | null
			: null;

		// If there's a saved game mode and it's different from current, restore it
		if (savedGameMode && savedGameMode !== gameState.gameMode) {
			console.log(`🔄 Restoring saved game mode: ${savedGameMode}`);
			room.send("changeGameMode", { gameMode: savedGameMode });
		}

		// Restore saved expansions
		const savedExpansionsStr = typeof window !== 'undefined'
			? localStorage.getItem('eorguessr_selected_expansions')
			: null;
		if (savedExpansionsStr) {
			const savedExpansions = JSON.parse(savedExpansionsStr) as string[];
			const currentExpansions = Array.from(gameState.selectedExpansions);
			// Only restore if different from current
			if (JSON.stringify(savedExpansions.sort()) !== JSON.stringify(currentExpansions.sort())) {
				console.log(`🔄 Restoring saved expansions:`, savedExpansions);
				room.send("setExpansions", { expansions: savedExpansions });
			}
		}

		// Restore saved map types
		const savedMapTypesStr = typeof window !== 'undefined'
			? localStorage.getItem('eorguessr_selected_maptypes')
			: null;
		if (savedMapTypesStr) {
			const savedMapTypes = JSON.parse(savedMapTypesStr) as string[];
			const currentMapTypes = Array.from(gameState.selectedMapTypes);
			// Only restore if different from current
			if (JSON.stringify(savedMapTypes.sort()) !== JSON.stringify(currentMapTypes.sort())) {
				console.log(`🔄 Restoring saved map types:`, savedMapTypes);
				room.send("setMapTypes", { mapTypes: savedMapTypes });
			}
		}

		hasRestoredGameMode.current = true;
	}, [room, gameState]);

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: 20 }}
			transition={{ duration: 0.3 }}
			className="absolute inset-0 flex z-20"
		>
			<div className="flex items-center justify-center w-full z-20">
				<div className="flex gap-10">
					<AnimatePresence mode="wait">
						{showSeedPanel ? (
							<SeedModePanel key="seed-panel" onClose={() => handleToggleSeedPanel(false)} />
						) : (
							<motion.div
								key="game-modes-panel"
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0, x: -20 }}
								transition={{ duration: 0.2 }}
							>
								<PanelFFXIV className="max-w-[90vw] max-h-[85vh] xl:max-h-[85vh] h-[777px] overflow-y-scroll 2xl:overflow-auto md:translate-x-[70px] 2xl:translate-x-[0] select-none">
									<div className="flex gap-3 flex-col md:flex-row">
										<div className="flex md:flex-col gap-4 overflow-scroll md:overflow-visible p-3 md:p-0">
											{availableGameModes.map(gameModeConfig => (
												<GameModeButton
													key={gameModeConfig.gameMode}
													gameMode={gameModeConfig.gameMode}
												/>
											))}
											<SeedModeButton
												isSelected={showSeedPanel}
												onClick={() => { play(AUDIO.CLICK); handleToggleSeedPanel(true); }}
											/>
										</div>
										{!isMobile && <VerticalGoldenSeparator />}
										{isMobile && <GameModeConfigSeparator />}
										<GameModeConfigPanel />
									</div>

						</PanelFFXIV>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
		{/* Disclaimer */}
		<div className="absolute bottom-2 left-0 right-0 text-center text-[9px] md:text-sm text-slate-300 text-shadow-border shadow-slate-900 px-4">
			<p>{t('disclaimer.unofficial')}</p>
			<p>{t('disclaimer.copyright').replace('{{year}}', currentYear.toString())}</p>
		</div>
	</motion.div>

	);
};

export default GameModesSoniop;